import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Building2, MapPin, Users, TrendingUp, Briefcase, ExternalLink, Flag, Link } from 'lucide-react'
import { useToast } from './ui/use-toast'

interface Company {
  id: string
  company_name: string
  logo_url: string | null
  description_short: string | null
  website_url: string | null
  employee_range: string | null
  funding_stage: string | null
  total_funding: string | null
  headquarters: string | null
  industry: string | null
  active_jobs: number | null
  founded_year: string | null
}

function Companies() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const [reportedCompanies, setReportedCompanies] = useState<Set<string>>(new Set())
  const [reportingCompanies, setReportingCompanies] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  useEffect(() => {
    fetchCompanies()
  }, [])

  async function fetchCompanies() {
    try {
      setLoading(true)

      // Get total count of companies
      const { count, error: countError } = await supabase
        .from('companies')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)

      if (countError) {
        console.error('Error fetching company count:', countError)
      } else {
        setTotalCount(count || 0)
      }

      // Fetch from companies table with specific columns
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('slug, name, logo_url, description_short, website_url, employee_range, funding_stage, headquarters, industry')
        .eq('is_active', true)
        .order('name', { ascending: true })

      if (companiesError) {
        console.error('Error fetching companies:', companiesError)
        throw companiesError
      }

      // Map to the Company interface
      const mappedCompanies = companiesData?.map(company => ({
        id: company.slug,
        company_name: company.name,
        logo_url: company.logo_url,
        description_short: company.description_short,
        website_url: company.website_url,
        employee_range: company.employee_range,
        funding_stage: company.funding_stage,
        total_funding: null,
        headquarters: company.headquarters,
        industry: company.industry,
        active_jobs: null,
        founded_year: null
      })) || []

      setCompanies(mappedCompanies)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch companies')
      console.error('Error fetching companies:', err)
    } finally {
      setLoading(false)
    }
  }

  function getCompanyInitials(companyName: string): string {
    const words = companyName.trim().split(/\s+/)
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    }
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  function formatFundingStage(stage: string | null): string {
    if (!stage || stage.toLowerCase() === 'unknown') return ''

    if (stage.toLowerCase().startsWith('series')) {
      const parts = stage.split('-')
      if (parts.length === 2) {
        return `Series ${parts[1].toUpperCase()}`
      }
    }

    return stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase()
  }

  function handleCompanyClick(company: Company) {
    // Navigate to individual company page using the slug (stored in id)
    navigate(`/company/${encodeURIComponent(company.id)}`)
  }

  function handleImageError(companyId: string) {
    setFailedImages(prev => new Set(prev).add(companyId))
  }

  // Handle reporting incorrect company data
  async function handleReportCompany(company: Company) {
    // Prevent duplicate submissions
    if (reportedCompanies.has(company.id) || reportingCompanies.has(company.id)) {
      return
    }

    setReportingCompanies(prev => new Set(prev).add(company.id))

    try {
      const response = await fetch('/api/report-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId: company.id,
          companyName: company.company_name,
          websiteUrl: company.website_url,
        })
      })

      if (response.ok) {
        setReportedCompanies(prev => new Set(prev).add(company.id))
        toast({
          description: "Thanks! We'll take it from here 🫡",
        })
      } else {
        toast({
          title: "Failed to submit report",
          description: "Please try again later.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error reporting company:', error)
      toast({
        title: "Failed to submit report",
        description: "Please try again later.",
        variant: "destructive",
      })
    } finally {
      setReportingCompanies(prev => {
        const newSet = new Set(prev)
        newSet.delete(company.id)
        return newSet
      })
    }
  }

  if (loading) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <div className="h-10 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-6 w-96 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                className="p-6 rounded-xl border border-gray-200 bg-white animate-pulse"
              >
                <div className="h-32 bg-gray-100 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            Error: {error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white pb-8">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Company Directory
          </h1>
          <p className="text-lg text-gray-600">
            Explore {totalCount.toLocaleString()} companies currently hiring on our platform
          </p>
        </div>

        {/* Companies Grid */}
        {companies.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-600">
            No companies available yet. Check back soon!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {companies.map((company) => (
              <div
                key={company.id}
                onClick={() => handleCompanyClick(company)}
                className="group p-6 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg transition-all duration-200 cursor-pointer"
              >
                {/* Company Logo */}
                <div className="flex items-start gap-4 mb-4">
                  {company.logo_url && !failedImages.has(company.id) ? (
                    <img
                      src={company.logo_url}
                      alt={company.company_name}
                      className="w-16 h-16 rounded-lg object-contain bg-white shrink-0"
                      onError={() => handleImageError(company.id)}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 text-white text-lg font-semibold shrink-0">
                      {getCompanyInitials(company.company_name)}
                    </div>
                  )}

                  {/* Company Name */}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                      {company.company_name}
                    </h2>
                    {company.industry && company.industry.toLowerCase() !== 'unknown' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                        {company.industry}
                      </span>
                    )}
                  </div>
                </div>

                {/* Company Description */}
                {company.description_short && (
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                    {company.description_short}
                  </p>
                )}

                {/* Company Stats */}
                <div className="space-y-2">
                  {/* Location */}
                  {company.headquarters && company.headquarters.toLowerCase() !== 'unknown' && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4 shrink-0" />
                      <span>{company.headquarters}</span>
                    </div>
                  )}

                  {/* Employee Count */}
                  {company.employee_range && company.employee_range.toLowerCase() !== 'unknown' && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="w-4 h-4 shrink-0" />
                      <span>{company.employee_range} employees</span>
                    </div>
                  )}

                  {/* Funding */}
                  {((company.funding_stage && company.funding_stage.toLowerCase() !== 'unknown') ||
                    (company.total_funding && company.total_funding.toLowerCase() !== 'unknown')) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <TrendingUp className="w-4 h-4 shrink-0" />
                      <span>
                        {company.funding_stage && formatFundingStage(company.funding_stage)}
                        {company.funding_stage && company.total_funding && company.total_funding.toLowerCase() !== 'unknown' && ' • '}
                        {company.total_funding && company.total_funding.toLowerCase() !== 'unknown' && company.total_funding}
                      </span>
                    </div>
                  )}

                  {/* Open Jobs */}
                  {company.active_jobs !== null && company.active_jobs > 0 && (
                    <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                      <Briefcase className="w-4 h-4 shrink-0" />
                      <span>{company.active_jobs} open {company.active_jobs === 1 ? 'position' : 'positions'}</span>
                    </div>
                  )}
                </div>

                {/* Footer: Website Link & Report Button */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  {company.website_url ? (
                    <a
                      href={company.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      <Link className="w-4 h-4" />
                      Visit Website
                    </a>
                  ) : (
                    <div></div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleReportCompany(company)
                    }}
                    disabled={reportedCompanies.has(company.id) || reportingCompanies.has(company.id)}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Flag className="w-3 h-3" />
                    {reportedCompanies.has(company.id)
                      ? 'report submitted'
                      : reportingCompanies.has(company.id)
                      ? 'submitting...'
                      : 'report incorrect data'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Companies
